import { Badge } from "@/components/ui/badge";
import { CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, Folder, FolderOpen, Loader2, Paperclip, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/pages/applications/inboxShared";

/** Inbox · Done · (Autopilot log) as tabs; every other mailbox folder under "Folders ▾" (Dean, 9 Oct 2026 — ten tabs wrapped to three rows). */
export function InboxTabs({ tabs, active, onChange, folders = [], activeFolder = null }) {
  const tabCls = (isActive) => cn("flex items-center justify-center gap-1.5 py-2 px-3 text-xs transition border-b-2 min-w-0",
    isActive ? "border-blue-600 font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800");
  return (
    <div className="flex border-b shrink-0 overflow-hidden" data-testid="inbox-tabs">
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} className={cn(tabCls(active === t.id), "flex-1")} title={t.title || t.label} data-testid={`inbox-tab-${t.id}`}>
          <t.icon className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{t.label}</span>
          {t.badge > 0 && <Badge className="text-[9px] h-4 px-1.5 bg-amber-500 hover:bg-amber-500">{t.badge}</Badge>}
          {t.count != null && <span className="text-[10px] text-slate-400 tabular-nums">{t.count}</span>}
        </button>
      ))}
      {folders.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={cn(tabCls(!!activeFolder), "flex-1 max-w-[220px]")} title={activeFolder ? activeFolder.path : `${folders.length} more folders`} data-testid="inbox-folders-menu-btn">
              {activeFolder ? <FolderOpen className="w-3.5 h-3.5 shrink-0" /> : <Folder className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate">{activeFolder ? activeFolder.name : "Folders"}</span>
              {activeFolder?.total != null && <span className="text-[10px] text-slate-400 tabular-nums">{activeFolder.total}</span>}
              {!activeFolder && <span className="text-[10px] text-slate-400 tabular-nums">{folders.length}</span>}
              <ChevronDown className="w-3 h-3 opacity-60 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[360px] overflow-auto min-w-[240px]" data-testid="inbox-folders-menu">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Mailbox folders</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {folders.map(f => (
              <DropdownMenuItem key={f.id} onClick={() => onChange(f.id)} className={cn("text-xs justify-between gap-3", activeFolder?.id === f.id && "font-semibold")} title={f.path} data-testid={`inbox-folder-${f.id}`}>
                <span className="truncate">{f.name}</span>
                <span className="text-[10px] text-slate-400 tabular-nums shrink-0">{f.total ?? ""}{f.unread ? ` · ${f.unread} unread` : ""}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/** Whole-mailbox search (Inbox, Done, sub-folders, Sent) — Enter to search, × to go back to the folder. */
export function InboxSearch({ value, onChange, onSearch, onClear, searching, active, resultCount }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b bg-slate-50/70 shrink-0" data-testid="inbox-search-bar">
      <div className="relative flex-1">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onSearch(); if (e.key === "Escape") onClear(); }}
          placeholder="Search every folder — sender, subject, SF number, company…" className="h-7 pl-7 pr-7 text-xs bg-white" data-testid="inbox-search-input" />
        {(value || active) && (
          <button type="button" onClick={onClear} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Clear search" data-testid="inbox-search-clear">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : active ? (
        <span className="text-[10px] text-slate-500 whitespace-nowrap tabular-nums" data-testid="inbox-search-count">{resultCount} result{resultCount === 1 ? "" : "s"} · all folders</span>
      ) : null}
    </div>
  );
}

export function EmailList({ emails, loading, hasData, selectedId, onSelect, loadingText, emptyText, renderBadges, selectable = false, checked, onToggle }) {
  return (
    <CardContent className="p-0 flex-1 min-h-0 overflow-y-auto divide-y" data-testid="inbox-email-list">
      {loading && !hasData && (
        <div className="p-6 text-center text-sm text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />{loadingText || "Loading inbox…"}
        </div>
      )}
      {!loading && emails.length === 0 && (
        <div className="p-6 text-center text-sm text-muted-foreground" data-testid="inbox-empty">{emptyText || "Inbox is empty."}</div>
      )}
      {emails.map(e => {
        const isChecked = !!checked?.has(e.id);
        return (
          <div key={e.id} className={cn("flex items-stretch group", selectedId === e.id && "bg-blue-50 border-l-2 border-blue-600", isChecked && "bg-amber-50/60")}>
            {selectable && (
              <div className={cn("flex items-start pt-3 pl-2 pr-0.5 shrink-0", !isChecked && "opacity-0 group-hover:opacity-100 focus-within:opacity-100")}
                onClick={(ev) => ev.stopPropagation()}>
                <Checkbox checked={isChecked} onCheckedChange={() => onToggle?.(e.id)} className="h-3.5 w-3.5" aria-label="Select email"
                  data-testid={`inbox-select-${e.internet_message_id || e.id}`} />
              </div>
            )}
            <button onClick={() => onSelect(e.id)} className="flex-1 min-w-0 text-left px-3 py-2.5 hover:bg-slate-50 transition"
              data-testid={`inbox-email-${e.internet_message_id || e.id}`}>
              <div className="flex items-center gap-2">
                <span className={cn("text-xs truncate flex-1", !e.is_read && "font-bold")}>{e.from_name || e.from_email}</span>
                {e.has_attachments && <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />}
                <span className="text-[10px] text-muted-foreground shrink-0">{fmtDate(e.received)}</span>
              </div>
              <div className={cn("text-xs truncate mt-0.5", !e.is_read ? "font-semibold" : "text-slate-700")}>{e.subject || "(no subject)"}</div>
              <div className="text-[11px] text-muted-foreground truncate">{e.preview}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {e.folder_name && (
                  <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-300 gap-0.5" title="Folder this email lives in" data-testid={`folder-badge-${e.internet_message_id || e.id}`}>
                    <Folder className="w-2.5 h-2.5" />{e.folder_name}
                  </Badge>
                )}
                {renderBadges ? renderBadges(e) : null}
              </div>
            </button>
          </div>
        );
      })}
    </CardContent>
  );
}
